import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, } from 'react';
import { Copy, Check, SquareArrowOutUpRight, BookmarkPlus, Unplug, Trash2, Info, } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger, } from '../../components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger, } from '../../components/shadcn/tooltip';
import { ImageWithFallback } from '@layerswap/ui-kit/components';
import { Address } from '../../lib/address/Address';
import { getExplorerUrl } from '../../lib/address/explorerUrl';
import clsx from 'clsx';
import { AddressLabelView } from './AddressLabelView';
export function AddressDetailsView({ address, network, providerName, isForCurrency, children, shouldShowChevron = true, showDetails, title, description, logo: Logo, displayName, label, isCopied = false, isPopoverOpen = false, saving = false, savingForm, canSave = false, readOnly, container, onPopoverOpenChange, onTooltipOpenChange, onCopy, onSave, onDisconnect, onRemove, }) {
    const addr = useMemo(() => new Address(address, network ?? null, providerName), [address, network, providerName]);
    const isAddressValid = network && Address.isValid(addr.full, network);
    const labeledAddress = label ??
        (displayName
            ? `${displayName} (${addr.toShortString()})`
            : addr.toShortString());
    // Resolver for action buttons
    const getActionButtons = () => {
        const buttons = [
            {
                title: 'Copy',
                Icon: isCopied ? Check : Copy,
                onClick: (e) => {
                    e.stopPropagation();
                    onCopy?.();
                },
            },
            ...(network && isAddressValid
                ? [
                    {
                        title: 'View',
                        Icon: SquareArrowOutUpRight,
                        href: readOnly
                            ? undefined
                            : getExplorerUrl(network.account_explorer_template, addr.full),
                    },
                ]
                : []),
            ...(canSave && !saving
                ? [
                    {
                        title: 'Save',
                        Icon: BookmarkPlus,
                        onClick: (e) => {
                            e.stopPropagation();
                            onSave?.();
                        },
                    },
                ]
                : []),
            ...(onDisconnect
                ? [
                    {
                        title: 'Disconnect',
                        Icon: Unplug,
                        iconClassNames: 'text-red-400',
                        onClick: (e) => {
                            e.stopPropagation();
                            onDisconnect();
                        },
                    },
                ]
                : []),
            ...(onRemove
                ? [
                    {
                        title: 'Remove',
                        Icon: Trash2,
                        iconClassNames: 'text-red-400',
                        onClick: (e) => {
                            e.stopPropagation();
                            onRemove();
                        },
                    },
                ]
                : []),
        ];
        const showTitles = buttons.length <= 2;
        return { buttons, showTitles };
    };
    const { buttons, showTitles } = getActionButtons();
    const { start, middle, end } = useMemo(() => addr.toEmphasizedParts(), [addr]);
    return (_jsx("div", { onClick: (e) => e.stopPropagation(), children: _jsxs(Popover, { open: isPopoverOpen, onOpenChange: onPopoverOpenChange, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx("div", { children: _jsxs(Tooltip, { onOpenChange: onTooltipOpenChange, children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx("span", { className: isForCurrency
                                            ? 'block w-full min-w-0'
                                            : undefined, children: children ?? (_jsx(AddressLabelView, { label: labeledAddress, saved: !!displayName, isForCurrency: isForCurrency, shouldShowChevron: shouldShowChevron })) }) }), _jsx(TooltipContent, { side: "bottom", className: "pointer-events-none", children: _jsx("p", { children: isForCurrency
                                            ? 'View token details'
                                            : 'View address details' }) })] }) }) }), _jsxs(PopoverContent, { container: container, className: "w-auto p-3 min-w-72 flex flex-col gap-3 items-stretch rounded-2xl! bg-secondary-500!", side: "top", avoidCollisions: true, collisionPadding: 8, sticky: "always", onInteractOutside: (e) => {
                        e.detail.originalEvent.stopPropagation();
                    }, onPointerDownOutside: (e) => {
                        e.detail.originalEvent.stopPropagation();
                    }, children: [showDetails && (title || description) && (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center gap-3", children: [Logo ? (_jsx(ImageWithFallback, { src: Logo, alt: title || 'Token logo', height: "40", width: "40", loading: "eager", fetchPriority: "high", className: "rounded-full object-contain shrink-0 h-10 w-10" })) : (_jsx(Info, { className: "w-10 h-10 text-secondary-text shrink-0" })), _jsxs("div", { className: "flex-1 font-medium", children: [title && (_jsx("h3", { className: "text-base leading-5 text-primary-text", children: title })), description && (_jsx("p", { className: "text-sm leading-4.5 text-secondary-text", children: description }))] })] }), _jsx("hr", { className: "border rounded-full border-secondary-400 mt-2" })] })), displayName && displayName !== title && (_jsxs("div", { className: "flex flex-wrap items-baseline justify-between gap-x-2 text-left", children: [_jsx("span", { className: "text-xs text-secondary-text shrink-0", children: "Saved as:" }), _jsx("span", { className: "text-primary-text text-sm font-medium break-words min-w-0", children: displayName })] })), _jsx("p", { className: "text-secondary-text text-sm leading-5 break-all text-left font-mono", children: _jsxs(_Fragment, { children: [_jsx("span", { className: "text-primary-text font-medium", children: start }), _jsx("span", { children: middle }), _jsx("span", { className: "text-primary-text font-medium", children: end })] }) }), _jsxs("div", { className: "space-y-1.5", children: [_jsx("div", { className: "flex gap-3", children: buttons.map((button) => (_jsx(ActionButton, { showTitle: showTitles, ...button }, button.title))) }), saving && savingForm] })] })] }) }));
}
const ActionButton = ({ title, Icon, onClick, href, iconClassNames, showTitle = true, }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const children = (_jsxs(_Fragment, { children: [_jsx(Icon, { className: clsx('h-3 w-3', iconClassNames) }), showTitle && _jsx("p", { className: "text-xs whitespace-nowrap", children: title })] }));
    const buttonClasses = 'cursor-pointer text-secondary-text hover:text-primary-text px-2.5 py-2 bg-secondary-300 hover:bg-secondary-400 rounded-lg transition-all duration-200 flex items-center gap-1 flex-1 justify-center';
    const renderButton = () => {
        if (href) {
            return (_jsx("a", { href: href, target: "_blank", className: buttonClasses, rel: "noopener noreferrer", children: children }));
        }
        return (_jsx("div", { onClick: onClick, className: buttonClasses, children: children }));
    };
    if (showTitle) {
        return renderButton();
    }
    return (_jsxs(Tooltip, { disableHoverableContent: true, open: showTooltip, onOpenChange: setShowTooltip, children: [_jsx(TooltipTrigger, { asChild: true, children: renderButton() }), _jsx(TooltipContent, { side: "top", children: _jsx("p", { children: title }) }, title)] }, title));
};
